import { ActionIcon, Box, Group, NumberInput, Paper, Skeleton, Text } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconChevronsLeft, IconChevronsRight } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { Document, DocumentProps, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

export function PdfPreview({ file }: { file: DocumentProps['file'] }) {
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    const [loading, setLoading] = useState(true);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState<number>(1);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setLoading(false);
        setNumPages(numPages);
        setPageNumber(1);
    };

    const changePage = (offset: number) => {
        setPageNumber((prev) => Math.min(Math.max(prev + offset, 1), numPages || 1));
    };

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        // Monitor container size adjustments accurately
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                // Track the component's inner width
                setContainerWidth(entry.contentRect.width);
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect();
    }, []);

    return (
        <Skeleton visible={loading} w="100%" h="100%">
            <Box pos="relative" bg="dimmed" bdrs="sm" style={{ flex: 1, width: '100%' }}>
                {/* Floating Toolbar Overlay */}
                <Paper
                    shadow="md"
                    radius="xl"
                    p="xs"
                    withBorder
                    pos="absolute"
                    bottom={32}
                    left="50%"
                    style={{
                        transform: 'translateX(-50%)',
                        zIndex: 10,
                        visibility: loading ? 'hidden' : 'visible',
                    }}
                >
                    <Group gap="xs" align="center">
                        {/* First Page */}
                        <ActionIcon variant="subtle" disabled={pageNumber <= 1} onClick={() => setPageNumber(1)}>
                            <IconChevronsLeft size={18} />
                        </ActionIcon>

                        {/* Previous Page */}
                        <ActionIcon variant="subtle" color="gray" disabled={pageNumber <= 1} onClick={() => changePage(-1)}>
                            <IconChevronLeft size={18} />
                        </ActionIcon>

                        {/* Jump to Page Input */}
                        <Group gap={4} align="center">
                            <NumberInput
                                value={pageNumber}
                                onChange={(val) => {
                                    if (typeof val === 'number' && val >= 1 && val <= (numPages || 1)) {
                                        setPageNumber(val);
                                    }
                                }}
                                min={1}
                                max={numPages || 1}
                                clampBehavior="strict"
                                hideControls
                                size="xs"
                                styles={{ input: { width: 45, textAlign: 'center' } }}
                            />
                            <Text size="sm" c="dimmed">
                                / {numPages || '--'}
                            </Text>
                        </Group>

                        {/* Next Page */}
                        <ActionIcon variant="subtle" color="gray" disabled={pageNumber >= (numPages || 1)} onClick={() => changePage(1)}>
                            <IconChevronRight size={18} />
                        </ActionIcon>

                        {/* Last Page */}
                        <ActionIcon variant="subtle" color="gray" disabled={pageNumber >= (numPages || 1)} onClick={() => setPageNumber(numPages || 1)}>
                            <IconChevronsRight size={18} />
                        </ActionIcon>
                    </Group>
                </Paper>

                {/* PDF Viewport */}
                <Box
                    p="md"
                    style={{
                        maxHeight: '80vh',
                        overflow: 'auto',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                    ref={containerRef}
                >
                    <Document file={file} onLoadSuccess={onDocumentLoadSuccess} loading={<Skeleton width={containerWidth} visible />}>
                        <Page pageNumber={pageNumber} renderAnnotationLayer={false} renderTextLayer={false} width={containerWidth || undefined} loading={<Skeleton width={containerWidth} visible />} />
                    </Document>
                </Box>
            </Box>
        </Skeleton>
    );
}
